<template>
  <div class="p-3 pb-16 bg mb-auto h-full overflow-auto bg-gradient-to-b from-gray-800 text-white" id="style-1">
    <h1 class="text-3xl font-bold mb-6">ประวัติสถิติเซิร์ฟเวอร์</h1>

    <!-- Loading State -->
    <div v-if="loading" class="text-center text-xl">กำลังโหลดข้อมูล...</div>
    <!-- Error State -->
    <div v-else-if="error" class="text-center text-xl text-red-400">{{ error }}</div>
    <!-- Data Table -->
    <div v-else>
      <div class="mb-4 flex justify-between items-center">
        <div>
          <label for="perPage" class="mr-2">จำนวนต่อหน้า:</label>
          <select v-model="perPage" id="perPage" class="bg-gray-700 text-white p-2 rounded">
            <option :value="10">10</option>
            <option :value="25">25</option>
            <option :value="50">50</option>
            <option :value="100">100</option>
          </select>
        </div>
        <div>ทั้งหมด: {{ stats.length }} รายการ</div>
      </div>

      <div class="overflow-x-auto">
        <table class="min-w-full bg-gray-700 rounded-xl shadow-lg">
          <thead>
            <tr class="bg-gray-600">
              <th class="py-3 px-4 text-left">วันที่บันทึก</th>
              <th class="py-3 px-4 text-left">ผู้ใช้ใหม่</th>
              <th class="py-3 px-4 text-left">ตัวละครใหม่</th>
              <th class="py-3 px-4 text-left">การเกิดใหม่</th>
              <th class="py-3 px-4 text-left">พลังงานที่ใช้</th>
              <th class="py-3 px-4 text-left">เงินจากการขาย</th>
              <th class="py-3 px-4 text-left">ลอบสังหารสำเร็จ</th>
              <th class="py-3 px-4 text-left">การเติมเงิน</th>
              <th class="py-3 px-4 text-left">รายละเอียดเพิ่มเติม</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="stat in paginatedStats" :key="stat.id" class="border-t border-gray-600 hover:bg-gray-600 transition-colors duration-200">
              <td class="py-3 px-4">{{ formatDate(stat.createdAt) }}</td>
              <td class="py-3 px-4">{{ stat.newUser || 0 }}</td>
              <td class="py-3 px-4">{{ stat.newCharacter || 0 }}</td>
              <td class="py-3 px-4">{{ stat.reborns || 0 }}</td>
              <td class="py-3 px-4">{{ stat.totalEnergyUsed || 0 }}</td>
              <td class="py-3 px-4">{{ stat.totalSilverFromItemSales || 0 }}</td>
              <td class="py-3 px-4">{{ stat.successfulAssassinations || 0 }}({{ stat.successfulSurvivorAssassinations }})</td>
              <td class="py-3 px-4">{{ stat.totalTopupRequests || 0 }}({{ stat.totalTopupAmount }})</td>
              <td class="py-3 px-4">
                <button @click="openDetailsModal(stat)" class="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-500 transition-colors duration-200">
                  ดูรายละเอียด
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div class="mt-4 flex justify-between items-center">
        <button @click="prevPage" :disabled="currentPage === 1" class="bg-gray-600 text-white px-4 py-2 rounded disabled:opacity-50 hover:bg-gray-500 transition-colors duration-200">
          ก่อนหน้า
        </button>
        <span>หน้า {{ currentPage }} / {{ totalPages }}</span>
        <button @click="nextPage" :disabled="currentPage === totalPages" class="bg-gray-600 text-white px-4 py-2 rounded disabled:opacity-50 hover:bg-gray-500 transition-colors duration-200">
          ถัดไป
        </button>
      </div>
    </div>

    <!-- Details Modal -->
    <transition name="fade">
      <div v-if="showModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 pt-4 overflow-y-auto">
        <div class="bg-gray-800 rounded-xl w-full max-w-4xl flex flex-col h-4/5 m-4">
          <!-- Modal Header -->
          <div class="p-4 border-b border-gray-700 flex justify-between items-center">
            <h2 class="text-2xl font-bold">รายละเอียดสถิติ (บันทึกเมื่อ: {{ formatDate(selectedStat.createdAt) }})</h2>
            <button @click="closeDetailsModal" class="text-red-400 hover:text-red-300 text-xl">✕</button>
          </div>
          <!-- Modal Body -->
          <div class="flex-1 overflow-y-auto p-6">
            <!-- ส่วนที่ 1: ข้อมูลผู้ใช้และตัวละคร -->
            <section class="mb-12">
              <h1 class="text-3xl font-bold mb-6">ข้อมูลผู้ใช้และตัวละคร</h1>
              <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                <div class="bg-gray-700 p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300">
                  <h2 class="text-xl font-semibold mb-2">ผู้ใช้ใหม่วันนี้</h2>
                  <p class="text-4xl text-yellow-400">{{ selectedStat.newUser || 0 }} คน</p>
                </div>
                <div class="bg-gray-700 p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300">
                  <h2 class="text-xl font-semibold mb-2">ตัวละครใหม่วันนี้</h2>
                  <p class="text-4xl text-yellow-400">{{ selectedStat.newCharacter || 0 }} ตัว</p>
                </div>
                <div class="bg-gray-700 p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300">
                  <h2 class="text-xl font-semibold mb-2">การเกิดใหม่</h2>
                  <p class="text-4xl text-yellow-400">{{ selectedStat.reborns || 0 }} ครั้ง</p>
                </div>
                <div class="bg-gray-700 p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300">
                  <h2 class="text-xl font-semibold mb-2">ผู้บุกรุกที่ถูกเอาชนะ</h2>
                  <p class="text-4xl text-yellow-400">{{ formatTimestamp(selectedStat.occupiersDefeated) }}</p>
                </div>
              </div>
            </section>

            <!-- ส่วนที่ 2: กิจกรรมการเล่น -->
            <section class="mb-12">
              <h1 class="text-3xl font-bold mb-6">กิจกรรมการเล่น</h1>
              <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                <div v-for="(item, index) in activityStats" :key="index" class="bg-gray-700 p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300">
                  <h2 class="text-xl font-semibold mb-2">{{ item.title }}</h2>
                  <p class="text-4xl text-yellow-400">{{ item.value }} {{ item.unit }}</p>
                </div>
              </div>
              <!-- Collapsible Table for Explorations by Map -->
              <div class="mb-6">
                <button @click="toggleSection('explorationsByMap')" class="w-full flex justify-between items-center bg-gray-600 p-4 rounded-xl text-xl font-semibold hover:bg-gray-500 transition-colors duration-200">
                  <span>การสำรวจตามแผนที่</span>
                  <svg :class="{ 'rotate-180': expandedSections.explorationsByMap }" class="w-6 h-6 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <transition name="fade">
                  <div v-if="expandedSections.explorationsByMap" class="mt-4 bg-gray-700 rounded-xl shadow-lg overflow-hidden">
                    <table class="min-w-full">
                      <thead>
                        <tr class="bg-gray-600">
                          <th class="py-3 px-6 text-left">แผนที่</th>
                          <th class="py-3 px-6 text-left">จำนวนครั้ง</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(value, key) in selectedStat.explorationsByMap || {}" :key="key" class="border-t border-gray-600 hover:bg-gray-600 transition-colors duration-200">
                          <td class="py-3 px-6">{{ key }}</td>
                          <td class="py-3 px-6 text-yellow-400">{{ value || 0 }}</td>
                        </tr>
                        <tr v-if="!Object.keys(selectedStat.explorationsByMap || {}).length" class="border-t border-gray-600">
                          <td colspan="2" class="py-3 px-6 text-center">ไม่มีข้อมูล</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </transition>
              </div>
              <!-- Collapsible Table for Explorations by Basement -->
              <div class="mb-6">
                <button @click="toggleSection('explorationsByBasement')" class="w-full flex justify-between items-center bg-gray-600 p-4 rounded-xl text-xl font-semibold hover:bg-gray-500 transition-colors duration-200">
                  <span>การสำรวจตามชั้นใต้ดิน</span>
                  <svg :class="{ 'rotate-180': expandedSections.explorationsByBasement }" class="w-6 h-6 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <transition name="fade">
                  <div v-if="expandedSections.explorationsByBasement" class="mt-4 bg-gray-700 rounded-xl shadow-lg overflow-hidden">
                    <table class="min-w-full">
                      <thead>
                        <tr class="bg-gray-600">
                          <th class="py-3 px-6 text-left">ชั้น</th>
                          <th class="py-3 px-6 text-left">จำนวนครั้ง</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(value, key) in selectedStat.explorationsByBasement || {}" :key="key" class="border-t border-gray-600 hover:bg-gray-600 transition-colors duration-200">
                          <td class="py-3 px-6">{{ key }}</td>
                          <td class="py-3 px-6 text-yellow-400">{{ value || 0 }}</td>
                        </tr>
                        <tr v-if="!Object.keys(selectedStat.explorationsByBasement || {}).length" class="border-t border-gray-600">
                          <td colspan="2" class="py-3 px-6 text-center">ไม่มีข้อมูล</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </transition>
              </div>
              <!-- Collapsible Table for Criminals Killed -->
              <div class="mb-6">
                <button @click="toggleSection('criminalsKilled')" class="w-full flex justify-between items-center bg-gray-600 p-4 rounded-xl text-xl font-semibold hover:bg-gray-500 transition-colors duration-200">
                  <span>อาชญากรที่ถูกฆ่า</span>
                  <svg :class="{ 'rotate-180': expandedSections.criminalsKilled }" class="w-6 h-6 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <transition name="fade">
                  <div v-if="expandedSections.criminalsKilled" class="mt-4 bg-gray-700 rounded-xl shadow-lg overflow-hidden">
                    <table class="min-w-full">
                      <thead>
                        <tr class="bg-gray-600">
                          <th class="py-3 px-6 text-left">อาชญากร</th>
                          <th class="py-3 px-6 text-left">จำนวน</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(value, key) in selectedStat.criminalsKilled || {}" :key="key" class="border-t border-gray-600 hover:bg-gray-600 transition-colors duration-200">
                          <td class="py-3 px-6">{{ key }}</td>
                          <td class="py-3 px-6 text-yellow-400">{{ value || 0 }}</td>
                        </tr>
                        <tr v-if="!Object.keys(selectedStat.criminalsKilled || {}).length" class="border-t border-gray-600">
                          <td colspan="2" class="py-3 px-6 text-center">ไม่มีข้อมูล</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </transition>
              </div>
              <!-- Collapsible Table for Mission Completions by ID -->
              <div class="mb-6">
                <button @click="toggleSection('missionCompletionsById')" class="w-full flex justify-between items-center bg-gray-600 p-4 rounded-xl text-xl font-semibold hover:bg-gray-500 transition-colors duration-200">
                  <span>ภารกิจที่สำเร็จตาม ID</span>
                  <svg :class="{ 'rotate-180': expandedSections.missionCompletionsById }" class="w-6 h-6 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <transition name="fade">
                  <div v-if="expandedSections.missionCompletionsById" class="mt-4 bg-gray-700 rounded-xl shadow-lg overflow-hidden">
                    <table class="min-w-full">
                      <thead>
                        <tr class="bg-gray-600">
                          <th class="py-3 px-6 text-left">ID ภารกิจ</th>
                          <th class="py-3 px-6 text-left">จำนวนครั้ง</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(value, key) in selectedStat.missionCompletionsById || {}" :key="key" class="border-t border-gray-600 hover:bg-gray-600 transition-colors duration-200">
                          <td class="py-3 px-6">{{ key }}</td>
                          <td class="py-3 px-6 text-yellow-400">{{ value || 0 }}</td>
                        </tr>
                        <tr v-if="!Object.keys(selectedStat.missionCompletionsById || {}).length" class="border-t border-gray-600">
                          <td colspan="2" class="py-3 px-6 text-center">ไม่มีข้อมูล</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </transition>
              </div>
              <!-- Collapsible Table for Daily Mission Completions by ID -->
              <div class="mb-6">
                <button @click="toggleSection('dailyMissionCompletionsById')" class="w-full flex justify-between items-center bg-gray-600 p-4 rounded-xl text-xl font-semibold hover:bg-gray-500 transition-colors duration-200">
                  <span>ภารกิจรายวันที่สำเร็จตาม ID</span>
                  <svg :class="{ 'rotate-180': expandedSections.dailyMissionCompletionsById }" class="w-6 h-6 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <transition name="fade">
                  <div v-if="expandedSections.dailyMissionCompletionsById" class="mt-4 bg-gray-700 rounded-xl shadow-lg overflow-hidden">
                    <table class="min-w-full">
                      <thead>
                        <tr class="bg-gray-600">
                          <th class="py-3 px-6 text-left">ID ภารกิจ</th>
                          <th class="py-3 px-6 text-left">จำนวนครั้ง</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(value, key) in selectedStat.dailyMissionCompletionsById || {}" :key="key" class="border-t border-gray-600 hover:bg-gray-600 transition-colors duration-200">
                          <td class="py-3 px-6">{{ key }}</td>
                          <td class="py-3 px-6 text-yellow-400">{{ value || 0 }}</td>
                        </tr>
                        <tr v-if="!Object.keys(selectedStat.dailyMissionCompletionsById || {}).length" class="border-t border-gray-600">
                          <td colspan="2" class="py-3 px-6 text-center">ไม่มีข้อมูล</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </transition>
              </div>
              <!-- Collapsible Table for Weekly Mission Completions by ID -->
              <div class="mb-6">
                <button @click="toggleSection('weeklyMissionCompletionsById')" class="w-full flex justify-between items-center bg-gray-600 p-4 rounded-xl text-xl font-semibold hover:bg-gray-500 transition-colors duration-200">
                  <span>ภารกิจรายสัปดาห์ที่สำเร็จตาม ID</span>
                  <svg :class="{ 'rotate-180': expandedSections.weeklyMissionCompletionsById }" class="w-6 h-6 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <transition name="fade">
                  <div v-if="expandedSections.weeklyMissionCompletionsById" class="mt-4 bg-gray-700 rounded-xl shadow-lg overflow-hidden">
                    <table class="min-w-full">
                      <thead>
                        <tr class="bg-gray-600">
                          <th class="py-3 px-6 text-left">ID ภารกิจ</th>
                          <th class="py-3 px-6 text-left">จำนวนครั้ง</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(value, key) in selectedStat.weeklyMissionCompletionsById || {}" :key="key" class="border-t border-gray-600 hover:bg-gray-600 transition-colors duration-200">
                          <td class="py-3 px-6">{{ key }}</td>
                          <td class="py-3 px-6 text-yellow-400">{{ value || 0 }}</td>
                        </tr>
                        <tr v-if="!Object.keys(selectedStat.weeklyMissionCompletionsById || {}).length" class="border-t border-gray-600">
                          <td colspan="2" class="py-3 px-6 text-center">ไม่มีข้อมูล</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </transition>
              </div>
              <!-- Collapsible Table for Ghost Activations By Item -->
              <div class="mb-6">
                <button @click="toggleSection('ghostActivationsByItem')" class="w-full flex justify-between items-center bg-gray-600 p-4 rounded-xl text-xl font-semibold hover:bg-gray-500 transition-colors duration-200">
                  <span>การกระตุ้นผีตามไอเทม</span>
                  <svg :class="{ 'rotate-180': expandedSections.ghostActivationsByItem }" class="w-6 h-6 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <transition name="fade">
                  <div v-if="expandedSections.ghostActivationsByItem" class="mt-4 bg-gray-700 rounded-xl shadow-lg overflow-hidden">
                    <table class="min-w-full">
                      <thead>
                        <tr class="bg-gray-600">
                          <th class="py-3 px-6 text-left">ไอเทม</th>
                          <th class="py-3 px-6 text-left">จำนวนครั้ง</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(value, key) in selectedStat.ghostActivationsByItem || {}" :key="key" class="border-t border-gray-600 hover:bg-gray-600 transition-colors duration-200">
                          <td class="py-3 px-6">{{ key }}</td>
                          <td class="py-3 px-6 text-yellow-400">{{ value || 0 }}</td>
                        </tr>
                        <tr v-if="!Object.keys(selectedStat.ghostActivationsByItem || {}).length" class="border-t border-gray-600">
                          <td colspan="2" class="py-3 px-6 text-center">ไม่มีข้อมูล</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </transition>
              </div>
              <!-- Collapsible Table for Ghost Cancellations By Type -->
              <div class="mb-6">
                <button @click="toggleSection('ghostCancellationsByType')" class="w-full flex justify-between items-center bg-gray-600 p-4 rounded-xl text-xl font-semibold hover:bg-gray-500 transition-colors duration-200">
                  <span>การยกเลิกผีตามประเภท</span>
                  <svg :class="{ 'rotate-180': expandedSections.ghostCancellationsByType }" class="w-6 h-6 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <transition name="fade">
                  <div v-if="expandedSections.ghostCancellationsByType" class="mt-4 bg-gray-700 rounded-xl shadow-lg overflow-hidden">
                    <table class="min-w-full">
                      <thead>
                        <tr class="bg-gray-600">
                          <th class="py-3 px-6 text-left">ประเภท</th>
                          <th class="py-3 px-6 text-left">จำนวนครั้ง</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(value, key) in selectedStat.ghostCancellationsByType || {}" :key="key" class="border-t border-gray-600 hover:bg-gray-600 transition-colors duration-200">
                          <td class="py-3 px-6">{{ key }}</td>
                          <td class="py-3 px-6 text-yellow-400">{{ value || 0 }}</td>
                        </tr>
                        <tr v-if="!Object.keys(selectedStat.ghostCancellationsByType || {}).length" class="border-t border-gray-600">
                          <td colspan="2" class="py-3 px-6 text-center">ไม่มีข้อมูล</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </transition>
              </div>
              <!-- Collapsible Table for Ghost Expirations By Type -->
              <div class="mb-6">
                <button @click="toggleSection('ghostExpirationsByType')" class="w-full flex justify-between items-center bg-gray-600 p-4 rounded-xl text-xl font-semibold hover:bg-gray-500 transition-colors duration-200">
                  <span>การหมดอายุผีตามประเภท</span>
                  <svg :class="{ 'rotate-180': expandedSections.ghostExpirationsByType }" class="w-6 h-6 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <transition name="fade">
                  <div v-if="expandedSections.ghostExpirationsByType" class="mt-4 bg-gray-700 rounded-xl shadow-lg overflow-hidden">
                    <table class="min-w-full">
                      <thead>
                        <tr class="bg-gray-600">
                          <th class="py-3 px-6 text-left">ประเภท</th>
                          <th class="py-3 px-6 text-left">จำนวนครั้ง</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(value, key) in selectedStat.ghostExpirationsByType || {}" :key="key" class="border-t border-gray-600 hover:bg-gray-600 transition-colors duration-200">
                          <td class="py-3 px-6">{{ key }}</td>
                          <td class="py-3 px-6 text-yellow-400">{{ value || 0 }}</td>
                        </tr>
                        <tr v-if="!Object.keys(selectedStat.ghostExpirationsByType || {}).length" class="border-t border-gray-600">
                          <td colspan="2" class="py-3 px-6 text-center">ไม่มีข้อมูล</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </transition>
              </div>
              <!-- Collapsible Table for Bomb Installs By Item -->
              <div class="mb-6">
                <button @click="toggleSection('bombInstallsByItem')" class="w-full flex justify-between items-center bg-gray-600 p-4 rounded-xl text-xl font-semibold hover:bg-gray-500 transition-colors duration-200">
                  <span>การติดตั้งระเบิดตามไอเทม</span>
                  <svg :class="{ 'rotate-180': expandedSections.bombInstallsByItem }" class="w-6 h-6 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <transition name="fade">
                  <div v-if="expandedSections.bombInstallsByItem" class="mt-4 bg-gray-700 rounded-xl shadow-lg overflow-hidden">
                    <table class="min-w-full">
                      <thead>
                        <tr class="bg-gray-600">
                          <th class="py-3 px-6 text-left">ไอเทม</th>
                          <th class="py-3 px-6 text-left">จำนวนครั้ง</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(value, key) in selectedStat.bombInstallsByItem || {}" :key="key" class="border-t border-gray-600 hover:bg-gray-600 transition-colors duration-200">
                          <td class="py-3 px-6">{{ key }}</td>
                          <td class="py-3 px-6 text-yellow-400">{{ value || 0 }}</td>
                        </tr>
                        <tr v-if="!Object.keys(selectedStat.bombInstallsByItem || {}).length" class="border-t border-gray-600">
                          <td colspan="2" class="py-3 px-6 text-center">ไม่มีข้อมูล</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </transition>
              </div>
            </section>

            <!-- ส่วนที่ 3: การจัดการไอเทมและทรัพยากร -->
            <section class="mb-12">
              <h1 class="text-3xl font-bold mb-6">การจัดการไอเทมและทรัพยากร</h1>
              <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                <div v-for="(item, index) in itemStats" :key="index" class="bg-gray-700 p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300">
                  <h2 class="text-xl font-semibold mb-2">{{ item.title }}</h2>
                  <p class="text-4xl text-yellow-400">{{ item.value }} {{ item.unit }}</p>
                </div>
              </div>
              <!-- Collapsible Tables -->
              <div v-for="(table, index) in collapsibleTables" :key="index" class="mb-6">
                <button @click="toggleSection(table.key)" class="w-full flex justify-between items-center bg-gray-600 p-4 rounded-xl text-xl font-semibold hover:bg-gray-500 transition-colors duration-200">
                  <span>{{ table.title }}</span>
                  <svg :class="{ 'rotate-180': expandedSections[table.key] }" class="w-6 h-6 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <transition name="fade">
                  <div v-if="expandedSections[table.key]" class="mt-4 bg-gray-700 rounded-xl shadow-lg overflow-hidden">
                    <table class="min-w-full">
                      <thead>
                        <tr class="bg-gray-600">
                          <th class="py-3 px-6 text-left">{{ table.header1 }}</th>
                          <th class="py-3 px-6 text-left">{{ table.header2 }}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(value, key) in selectedStat[table.dataKey] || {}" :key="key" class="border-t border-gray-600 hover:bg-gray-600 transition-colors duration-200">
                          <td class="py-3 px-6">{{ key }}</td>
                          <td class="py-3 px-6 text-yellow-400">{{ value || 0 }}</td>
                        </tr>
                        <tr v-if="!Object.keys(selectedStat[table.dataKey] || {}).length" class="border-t border-gray-600">
                          <td colspan="2" class="py-3 px-6 text-center">ไม่มีข้อมูล</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </transition>
              </div>
              <!-- Collapsible Table for Items Crafted -->
              <div class="mb-6">
                <button @click="toggleSection('itemsCrafted')" class="w-full flex justify-between items-center bg-gray-600 p-4 rounded-xl text-xl font-semibold hover:bg-gray-500 transition-colors duration-200">
                  <span>ไอเทมที่คราฟ</span>
                  <svg :class="{ 'rotate-180': expandedSections.itemsCrafted }" class="w-6 h-6 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <transition name="fade">
                  <div v-if="expandedSections.itemsCrafted" class="mt-4 bg-gray-700 rounded-xl shadow-lg overflow-hidden">
                    <table class="min-w-full">
                      <thead>
                        <tr class="bg-gray-600">
                          <th class="py-3 px-6 text-left">ไอเทม</th>
                          <th class="py-3 px-6 text-left">จำนวน</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(value, key) in selectedStat.itemsCrafted || {}" :key="key" class="border-t border-gray-600 hover:bg-gray-600 transition-colors duration-200">
                          <td class="py-3 px-6">{{ key }}</td>
                          <td class="py-3 px-6 text-yellow-400">{{ value || 0 }}</td>
                        </tr>
                        <tr v-if="!Object.keys(selectedStat.itemsCrafted || {}).length" class="border-t border-gray-600">
                          <td colspan="2" class="py-3 px-6 text-center">ไม่มีข้อมูล</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </transition>
              </div>
              <!-- Collapsible Table for Items From Mysterious Box -->
              <div class="mb-6">
                <button @click="toggleSection('itemsFromMysteriousBox')" class="w-full flex justify-between items-center bg-gray-600 p-4 rounded-xl text-xl font-semibold hover:bg-gray-500 transition-colors duration-200">
                  <span>ไอเทมจากกล่องปริศนา</span>
                  <svg :class="{ 'rotate-180': expandedSections.itemsFromMysteriousBox }" class="w-6 h-6 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <transition name="fade">
                  <div v-if="expandedSections.itemsFromMysteriousBox" class="mt-4 bg-gray-700 rounded-xl shadow-lg overflow-hidden">
                    <table class="min-w-full">
                      <thead>
                        <tr class="bg-gray-600">
                          <th class="py-3 px-6 text-left">ไอเทม</th>
                          <th class="py-3 px-6 text-left">จำนวน</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(value, key) in selectedStat.itemsFromMysteriousBox || {}" :key="key" class="border-t border-gray-600 hover:bg-gray-600 transition-colors duration-200">
                          <td class="py-3 px-6">{{ key }}</td>
                          <td class="py-3 px-6 text-yellow-400">{{ value || 0 }}</td>
                        </tr>
                        <tr v-if="!Object.keys(selectedStat.itemsFromMysteriousBox || {}).length" class="border-t border-gray-600">
                          <td colspan="2" class="py-3 px-6 text-center">ไม่มีข้อมูล</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </transition>
              </div>
              <!-- Collapsible Table for Healed By Item -->
              <div class="mb-6">
                <button @click="toggleSection('healedByItem')" class="w-full flex justify-between items-center bg-gray-600 p-4 rounded-xl text-xl font-semibold hover:bg-gray-500 transition-colors duration-200">
                  <span>การรักษาโดยไอเทม</span>
                  <svg :class="{ 'rotate-180': expandedSections.healedByItem }" class="w-6 h-6 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <transition name="fade">
                  <div v-if="expandedSections.healedByItem" class="mt-4 bg-gray-700 rounded-xl shadow-lg overflow-hidden">
                    <table class="min-w-full">
                      <thead>
                        <tr class="bg-gray-600">
                          <th class="py-3 px-6 text-left">ไอเทม</th>
                          <th class="py-3 px-6 text-left">จำนวนครั้ง</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(value, key) in selectedStat.healedByItem || {}" :key="key" class="border-t border-gray-600 hover:bg-gray-600 transition-colors duration-200">
                          <td class="py-3 px-6">{{ key }}</td>
                          <td class="py-3 px-6 text-yellow-400">{{ value || 0 }}</td>
                        </tr>
                        <tr v-if="!Object.keys(selectedStat.healedByItem || {}).length" class="border-t border-gray-600">
                          <td colspan="2" class="py-3 px-6 text-center">ไม่มีข้อมูล</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </transition>
              </div>
              <!-- Collapsible Table for Trap Installations By Item -->
              <div class="mb-6">
                <button @click="toggleSection('trapInstallationsByItem')" class="w-full flex justify-between items-center bg-gray-600 p-4 rounded-xl text-xl font-semibold hover:bg-gray-500 transition-colors duration-200">
                  <span>การติดตั้งกับดักตามไอเทม</span>
                  <svg :class="{ 'rotate-180': expandedSections.trapInstallationsByItem }" class="w-6 h-6 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <transition name="fade">
                  <div v-if="expandedSections.trapInstallationsByItem" class="mt-4 bg-gray-700 rounded-xl shadow-lg overflow-hidden">
                    <table class="min-w-full">
                      <thead>
                        <tr class="bg-gray-600">
                          <th class="py-3 px-6 text-left">ไอเทม</th>
                          <th class="py-3 px-6 text-left">จำนวนครั้ง</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(value, key) in selectedStat.trapInstallationsByItem || {}" :key="key" class="border-t border-gray-600 hover:bg-gray-600 transition-colors duration-200">
                          <td class="py-3 px-6">{{ key }}</td>
                          <td class="py-3 px-6 text-yellow-400">{{ value || 0 }}</td>
                        </tr>
                        <tr v-if="!Object.keys(selectedStat.trapInstallationsByItem || {}).length" class="border-t border-gray-600">
                          <td colspan="2" class="py-3 px-6 text-center">ไม่มีข้อมูล</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </transition>
              </div>
              <!-- Collapsible Table for Trap Disarms By Item -->
              <div class="mb-6">
                <button @click="toggleSection('trapDisarmsByItem')" class="w-full flex justify-between items-center bg-gray-600 p-4 rounded-xl text-xl font-semibold hover:bg-gray-500 transition-colors duration-200">
                  <span>การปลดกับดักตามไอเทม</span>
                  <svg :class="{ 'rotate-180': expandedSections.trapDisarmsByItem }" class="w-6 h-6 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <transition name="fade">
                  <div v-if="expandedSections.trapDisarmsByItem" class="mt-4 bg-gray-700 rounded-xl shadow-lg overflow-hidden">
                    <table class="min-w-full">
                      <thead>
                        <tr class="bg-gray-600">
                          <th class="py-3 px-6 text-left">ไอเทม</th>
                          <th class="py-3 px-6 text-left">จำนวนครั้ง</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(value, key) in selectedStat.trapDisarmsByItem || {}" :key="key" class="border-t border-gray-600 hover:bg-gray-600 transition-colors duration-200">
                          <td class="py-3 px-6">{{ key }}</td>
                          <td class="py-3 px-6 text-yellow-400">{{ value || 0 }}</td>
                        </tr>
                        <tr v-if="!Object.keys(selectedStat.trapDisarmsByItem || {}).length" class="border-t border-gray-600">
                          <td colspan="2" class="py-3 px-6 text-center">ไม่มีข้อมูล</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </transition>
              </div>
              <!-- Collapsible Table for Trap Cancellations By Type -->
              <div class="mb-6">
                <button @click="toggleSection('trapCancellationsByType')" class="w-full flex justify-between items-center bg-gray-600 p-4 rounded-xl text-xl font-semibold hover:bg-gray-500 transition-colors duration-200">
                  <span>การยกเลิกกับดักตามประเภท</span>
                  <svg :class="{ 'rotate-180': expandedSections.trapCancellationsByType }" class="w-6 h-6 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <transition name="fade">
                  <div v-if="expandedSections.trapCancellationsByType" class="mt-4 bg-gray-700 rounded-xl shadow-lg overflow-hidden">
                    <table class="min-w-full">
                      <thead>
                        <tr class="bg-gray-600">
                          <th class="py-3 px-6 text-left">ประเภท</th>
                          <th class="py-3 px-6 text-left">จำนวนครั้ง</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(value, key) in selectedStat.trapCancellationsByType || {}" :key="key" class="border-t border-gray-600 hover:bg-gray-600 transition-colors duration-200">
                          <td class="py-3 px-6">{{ key }}</td>
                          <td class="py-3 px-6 text-yellow-400">{{ value || 0 }}</td>
                        </tr>
                        <tr v-if="!Object.keys(selectedStat.trapCancellationsByType || {}).length" class="border-t border-gray-600">
                          <td colspan="2" class="py-3 px-6 text-center">ไม่มีข้อมูล</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </transition>
              </div>
              <!-- Collapsible Table for Rank Bonus Rewards -->
              <div class="mb-6">
                <button @click="toggleSection('rankBonusRewards')" class="w-full flex justify-between items-center bg-gray-600 p-4 rounded-xl text-xl font-semibold hover:bg-gray-500 transition-colors duration-200">
                  <span>รางวัลโบนัสอันดับ</span>
                  <svg :class="{ 'rotate-180': expandedSections.rankBonusRewards }" class="w-6 h-6 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <transition name="fade">
                  <div v-if="expandedSections.rankBonusRewards" class="mt-4 bg-gray-700 rounded-xl shadow-lg overflow-hidden">
                    <table class="min-w-full">
                      <thead>
                        <tr class="bg-gray-600">
                          <th class="py-3 px-6 text-left">ประเภท</th>
                          <th class="py-3 px-6 text-left">จำนวน</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(value, key) in selectedStat.rankBonusRewards || {}" :key="key" class="border-t border-gray-600 hover:bg-gray-600 transition-colors duration-200">
                          <td class="py-3 px-6">{{ key }}</td>
                          <td class="py-3 px-6 text-yellow-400">{{ value || 0 }}</td>
                        </tr>
                        <tr v-if="!Object.keys(selectedStat.rankBonusRewards || {}).length" class="border-t border-gray-600">
                          <td colspan="2" class="py-3 px-6 text-center">ไม่มีข้อมูล</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </transition>
              </div>
              <!-- Collapsible Table for Total Resources Used For Storage Upgrades -->
              <div class="mb-6">
                <button @click="toggleSection('totalResourcesUsedForStorageUpgrades')" class="w-full flex justify-between items-center bg-gray-600 p-4 rounded-xl text-xl font-semibold hover:bg-gray-500 transition-colors duration-200">
                  <span>ทรัพยากรที่ใช้ในการอัปเกรดที่เก็บ</span>
                  <svg :class="{ 'rotate-180': expandedSections.totalResourcesUsedForStorageUpgrades }" class="w-6 h-6 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <transition name="fade">
                  <div v-if="expandedSections.totalResourcesUsedForStorageUpgrades" class="mt-4 bg-gray-700 rounded-xl shadow-lg overflow-hidden">
                    <table class="min-w-full">
                      <thead>
                        <tr class="bg-gray-600">
                          <th class="py-3 px-6 text-left">ทรัพยากร</th>
                          <th class="py-3 px-6 text-left">จำนวน</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(value, key) in selectedStat.totalResourcesUsedForStorageUpgrades || {}" :key="key" class="border-t border-gray-600 hover:bg-gray-600 transition-colors duration-200">
                          <td class="py-3 px-6">{{ key }}</td>
                          <td class="py-3 px-6 text-yellow-400">{{ value || 0 }}</td>
                        </tr>
                        <tr v-if="!Object.keys(selectedStat.totalResourcesUsedForStorageUpgrades || {}).length" class="border-t border-gray-600">
                          <td colspan="2" class="py-3 px-6 text-center">ไม่มีข้อมูล</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </transition>
              </div>
              <!-- Collapsible Table for Items Listed In Marketplace -->
              <div class="mb-6">
                <button @click="toggleSection('itemsListedInMarketplace')" class="w-full flex justify-between items-center bg-gray-600 p-4 rounded-xl text-xl font-semibold hover:bg-gray-500 transition-colors duration-200">
                  <span>ไอเทมที่ลงรายการในตลาด</span>
                  <svg :class="{ 'rotate-180': expandedSections.itemsListedInMarketplace }" class="w-6 h-6 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <transition name="fade">
                  <div v-if="expandedSections.itemsListedInMarketplace" class="mt-4 bg-gray-700 rounded-xl shadow-lg overflow-hidden">
                    <table class="min-w-full">
                      <thead>
                        <tr class="bg-gray-600">
                          <th class="py-3 px-6 text-left">จำนวน</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr class="border-t border-gray-600 hover:bg-gray-600 transition-colors duration-200">
                          <td class="py-3 px-6 text-yellow-400 text-center">{{ selectedStat.itemsListedInMarketplace || 0 }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </transition>
              </div>
              <!-- Collapsible Table for Items Returned From Marketplace -->
              <div class="mb-6">
                <button @click="toggleSection('itemsReturnedFromMarketplace')" class="w-full flex justify-between items-center bg-gray-600 p-4 rounded-xl text-xl font-semibold hover:bg-gray-500 transition-colors duration-200">
                  <span>ไอเทมที่คืนจากตลาด</span>
                  <svg :class="{ 'rotate-180': expandedSections.itemsReturnedFromMarketplace }" class="w-6 h-6 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <transition name="fade">
                  <div v-if="expandedSections.itemsReturnedFromMarketplace" class="mt-4 bg-gray-700 rounded-xl shadow-lg overflow-hidden">
                    <table class="min-w-full">
                      <thead>
                        <tr class="bg-gray-600">
                          <th class="py-3 px-6 text-left">จำนวน</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr class="border-t border-gray-600 hover:bg-gray-600 transition-colors duration-200">
                          <td class="py-3 px-6 text-yellow-400 text-center">{{ selectedStat.itemsReturnedFromMarketplace || 0 }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </transition>
              </div>
              <!-- Collapsible Table for Items Turned To Trash -->
              <div class="mb-6">
                <button @click="toggleSection('itemsTurnedToTrash')" class="w-full flex justify-between items-center bg-gray-600 p-4 rounded-xl text-xl font-semibold hover:bg-gray-500 transition-colors duration-200">
                  <span>ไอเทมที่กลายเป็นขยะ</span>
                  <svg :class="{ 'rotate-180': expandedSections.itemsTurnedToTrash }" class="w-6 h-6 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <transition name="fade">
                  <div v-if="expandedSections.itemsTurnedToTrash" class="mt-4 bg-gray-700 rounded-xl shadow-lg overflow-hidden">
                    <table class="min-w-full">
                      <thead>
                        <tr class="bg-gray-600">
                          <th class="py-3 px-6 text-left">จำนวน</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr class="border-t border-gray-600 hover:bg-gray-600 transition-colors duration-200">
                          <td class="py-3 px-6 text-yellow-400 text-center">{{ selectedStat.itemsTurnedToTrash || 0 }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </transition>
              </div>
              <!-- Collapsible Table for Loot Cancellations By Item -->
              <div class="mb-6">
                <button @click="toggleSection('lootCancellationsByItem')" class="w-full flex justify-between items-center bg-gray-600 p-4 rounded-xl text-xl font-semibold hover:bg-gray-500 transition-colors duration-200">
                  <span>การยกเลิกของ loot ตามไอเทม</span>
                  <svg :class="{ 'rotate-180': expandedSections.lootCancellationsByItem }" class="w-6 h-6 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <transition name="fade">
                  <div v-if="expandedSections.lootCancellationsByItem" class="mt-4 bg-gray-700 rounded-xl shadow-lg overflow-hidden">
                    <table class="min-w-full">
                      <thead>
                        <tr class="bg-gray-600">
                          <th class="py-3 px-6 text-left">ไอเทม</th>
                          <th class="py-3 px-6 text-left">จำนวนครั้ง</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(value, key) in selectedStat.lootCancellationsByItem || {}" :key="key" class="border-t border-gray-600 hover:bg-gray-600 transition-colors duration-200">
                          <td class="py-3 px-6">{{ key }}</td>
                          <td class="py-3 px-6 text-yellow-400">{{ value || 0 }}</td>
                        </tr>
                        <tr v-if="!Object.keys(selectedStat.lootCancellationsByItem || {}).length" class="border-t border-gray-600">
                          <td colspan="2" class="py-3 px-6 text-center">ไม่มีข้อมูล</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </transition>
              </div>
              <!-- Collapsible Table for Loot Installations By Item -->
              <div class="mb-6">
                <button @click="toggleSection('lootInstallationsByItem')" class="w-full flex justify-between items-center bg-gray-600 p-4 rounded-xl text-xl font-semibold hover:bg-gray-500 transition-colors duration-200">
                  <span>การติดตั้ง loot ตามไอเทม</span>
                  <svg :class="{ 'rotate-180': expandedSections.lootInstallationsByItem }" class="w-6 h-6 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <transition name="fade">
                  <div v-if="expandedSections.lootInstallationsByItem" class="mt-4 bg-gray-700 rounded-xl shadow-lg overflow-hidden">
                    <table class="min-w-full">
                      <thead>
                        <tr class="bg-gray-600">
                          <th class="py-3 px-6 text-left">ไอเทม</th>
                          <th class="py-3 px-6 text-left">จำนวนครั้ง</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(value, key) in selectedStat.lootInstallationsByItem || {}" :key="key" class="border-t border-gray-600 hover:bg-gray-600 transition-colors duration-200">
                          <td class="py-3 px-6">{{ key }}</td>
                          <td class="py-3 px-6 text-yellow-400">{{ value || 0 }}</td>
                        </tr>
                        <tr v-if="!Object.keys(selectedStat.lootInstallationsByItem || {}).length" class="border-t border-gray-600">
                          <td colspan="2" class="py-3 px-6 text-center">ไม่มีข้อมูล</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </transition>
              </div>
              <!-- Collapsible Table for Waiting Loot Expirations By Item -->
              <div class="mb-6">
                <button @click="toggleSection('waitingLootExpirationsByItem')" class="w-full flex justify-between items-center bg-gray-600 p-4 rounded-xl text-xl font-semibold hover:bg-gray-500 transition-colors duration-200">
                  <span>การหมดอายุ loot ที่รอตามไอเทม</span>
                  <svg :class="{ 'rotate-180': expandedSections.waitingLootExpirationsByItem }" class="w-6 h-6 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <transition name="fade">
                  <div v-if="expandedSections.waitingLootExpirationsByItem" class="mt-4 bg-gray-700 rounded-xl shadow-lg overflow-hidden">
                    <table class="min-w-full">
                      <thead>
                        <tr class="bg-gray-600">
                          <th class="py-3 px-6 text-left">ไอเทม</th>
                          <th class="py-3 px-6 text-left">จำนวนครั้ง</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(value, key) in selectedStat.waitingLootExpirationsByItem || {}" :key="key" class="border-t border-gray-600 hover:bg-gray-600 transition-colors duration-200">
                          <td class="py-3 px-6">{{ key }}</td>
                          <td class="py-3 px-6 text-yellow-400">{{ value || 0 }}</td>
                        </tr>
                        <tr v-if="!Object.keys(selectedStat.waitingLootExpirationsByItem || {}).length" class="border-t border-gray-600">
                          <td colspan="2" class="py-3 px-6 text-center">ไม่มีข้อมูล</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </transition>
              </div>
            </section>

            <!-- ส่วนที่ 4: การซื้อและการขายไอเทม -->
            <section class="mb-12">
              <h1 class="text-3xl font-bold mb-6">การซื้อและการขายไอเทม</h1>
              <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                <div v-for="(item, index) in purchaseSaleStats" :key="index" class="bg-gray-700 p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300">
                  <h2 class="text-xl font-semibold mb-2">{{ item.title }}</h2>
                  <p class="text-4xl text-yellow-400">{{ item.value }} {{ item.unit }}</p>
                </div>
              </div>
              <!-- Collapsible Tables for Purchase/Sale Maps -->
              <div v-for="(table, index) in collapsiblePurchaseSaleTables" :key="index" class="mb-6">
                <button @click="toggleSection(table.key)" class="w-full flex justify-between items-center bg-gray-600 p-4 rounded-xl text-xl font-semibold hover:bg-gray-500 transition-colors duration-200">
                  <span>{{ table.title }}</span>
                  <svg :class="{ 'rotate-180': expandedSections[table.key] }" class="w-6 h-6 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <transition name="fade">
                  <div v-if="expandedSections[table.key]" class="mt-4 bg-gray-700 rounded-xl shadow-lg overflow-hidden">
                    <table class="min-w-full">
                      <thead>
                        <tr class="bg-gray-600">
                          <th class="py-3 px-6 text-left">{{ table.header1 }}</th>
                          <th class="py-3 px-6 text-left">{{ table.header2 }}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(value, key) in selectedStat[table.dataKey] || {}" :key="key" class="border-t border-gray-600 hover:bg-gray-600 transition-colors duration-200">
                          <td class="py-3 px-6">{{ key }}</td>
                          <td class="py-3 px-6 text-yellow-400">{{ value || 0 }}</td>
                        </tr>
                        <tr v-if="!Object.keys(selectedStat[table.dataKey] || {}).length" class="border-t border-gray-600">
                          <td colspan="2" class="py-3 px-6 text-center">ไม่มีข้อมูล</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </transition>
              </div>
              <!-- Collapsible Table for Marketplace Diamonds Traded -->
              <div class="mb-6">
                <button @click="toggleSection('marketplaceDiamondsTraded')" class="w-full flex justify-between items-center bg-gray-600 p-4 rounded-xl text-xl font-semibold hover:bg-gray-500 transition-colors duration-200">
                  <span>เพชรที่แลกเปลี่ยนในตลาด</span>
                  <svg :class="{ 'rotate-180': expandedSections.marketplaceDiamondsTraded }" class="w-6 h-6 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <transition name="fade">
                  <div v-if="expandedSections.marketplaceDiamondsTraded" class="mt-4 bg-gray-700 rounded-xl shadow-lg overflow-hidden">
                    <table class="min-w-full">
                      <thead>
                        <tr class="bg-gray-600">
                          <th class="py-3 px-6 text-left">จำนวน</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr class="border-t border-gray-600 hover:bg-gray-600 transition-colors duration-200">
                          <td class="py-3 px-6 text-yellow-400 text-center">{{ selectedStat.marketplaceDiamondsTraded || 0 }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </transition>
              </div>
              <!-- Collapsible Table for Marketplace Items Sold -->
              <div class="mb-6">
                <button @click="toggleSection('marketplaceItemsSold')" class="w-full flex justify-between items-center bg-gray-600 p-4 rounded-xl text-xl font-semibold hover:bg-gray-500 transition-colors duration-200">
                  <span>ไอเทมที่ขายในตลาด</span>
                  <svg :class="{ 'rotate-180': expandedSections.marketplaceItemsSold }" class="w-6 h-6 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <transition name="fade">
                  <div v-if="expandedSections.marketplaceItemsSold" class="mt-4 bg-gray-700 rounded-xl shadow-lg overflow-hidden">
                    <table class="min-w-full">
                      <thead>
                        <tr class="bg-gray-600">
                          <th class="py-3 px-6 text-left">จำนวน</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr class="border-t border-gray-600 hover:bg-gray-600 transition-colors duration-200">
                          <td class="py-3 px-6 text-yellow-400 text-center">{{ selectedStat.marketplaceItemsSold || 0 }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </transition>
              </div>
              <!-- Collapsible Table for Marketplace Shops Closed -->
              <div class="mb-6">
                <button @click="toggleSection('marketplaceShopsClosed')" class="w-full flex justify-between items-center bg-gray-600 p-4 rounded-xl text-xl font-semibold hover:bg-gray-500 transition-colors duration-200">
                  <span>ร้านค้าตลาดที่ปิด</span>
                  <svg :class="{ 'rotate-180': expandedSections.marketplaceShopsClosed }" class="w-6 h-6 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <transition name="fade">
                  <div v-if="expandedSections.marketplaceShopsClosed" class="mt-4 bg-gray-700 rounded-xl shadow-lg overflow-hidden">
                    <table class="min-w-full">
                      <thead>
                        <tr class="bg-gray-600">
                          <th class="py-3 px-6 text-left">จำนวน</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr class="border-t border-gray-600 hover:bg-gray-600 transition-colors duration-200">
                          <td class="py-3 px-6 text-yellow-400 text-center">{{ selectedStat.marketplaceShopsClosed || 0 }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </transition>
              </div>
              <!-- Collapsible Table for Marketplace Shops Created -->
              <div class="mb-6">
                <button @click="toggleSection('marketplaceShopsCreated')" class="w-full flex justify-between items-center bg-gray-600 p-4 rounded-xl text-xl font-semibold hover:bg-gray-500 transition-colors duration-200">
                  <span>ร้านค้าตลาดที่สร้าง</span>
                  <svg :class="{ 'rotate-180': expandedSections.marketplaceShopsCreated }" class="w-6 h-6 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <transition name="fade">
                  <div v-if="expandedSections.marketplaceShopsCreated" class="mt-4 bg-gray-700 rounded-xl shadow-lg overflow-hidden">
                    <table class="min-w-full">
                      <thead>
                        <tr class="bg-gray-600">
                          <th class="py-3 px-6 text-left">จำนวน</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr class="border-t border-gray-600 hover:bg-gray-600 transition-colors duration-200">
                          <td class="py-3 px-6 text-yellow-400 text-center">{{ selectedStat.marketplaceShopsCreated || 0 }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </transition>
              </div>
              <!-- Collapsible Table for Marketplace Tax Collected -->
              <div class="mb-6">
                <button @click="toggleSection('marketplaceTaxCollected')" class="w-full flex justify-between items-center bg-gray-600 p-4 rounded-xl text-xl font-semibold hover:bg-gray-500 transition-colors duration-200">
                  <span>ภาษีตลาดที่เก็บได้</span>
                  <svg :class="{ 'rotate-180': expandedSections.marketplaceTaxCollected }" class="w-6 h-6 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <transition name="fade">
                  <div v-if="expandedSections.marketplaceTaxCollected" class="mt-4 bg-gray-700 rounded-xl shadow-lg overflow-hidden">
                    <table class="min-w-full">
                      <thead>
                        <tr class="bg-gray-600">
                          <th class="py-3 px-6 text-left">จำนวน</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr class="border-t border-gray-600 hover:bg-gray-600 transition-colors duration-200">
                          <td class="py-3 px-6 text-yellow-400 text-center">{{ selectedStat.marketplaceTaxCollected || 0 }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </transition>
              </div>
            </section>

            <!-- ส่วนที่ 5: การอัปเกรดและการพัฒนา -->
            <section class="mb-12">
              <h1 class="text-3xl font-bold mb-6">การอัปเกรดและการพัฒนา</h1>
              <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                <div v-for="(item, index) in upgradeStats" :key="index" class="bg-gray-700 p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300">
                  <h2 class="text-xl font-semibold mb-2">{{ item.title }}</h2>
                  <p class="text-4xl text-yellow-400">{{ item.value }} {{ item.unit }}</p>
                </div>
              </div>
              <!-- Collapsible Upgrade Tables -->
              <div v-for="(table, index) in collapsibleUpgradeTables" :key="index" class="mb-6">
                <button @click="toggleSection(table.key)" class="w-full flex justify-between items-center bg-gray-600 p-4 rounded-xl text-xl font-semibold hover:bg-gray-500 transition-colors duration-200">
                  <span>{{ table.title }}</span>
                  <svg :class="{ 'rotate-180': expandedSections[table.key] }" class="w-6 h-6 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <transition name="fade">
                  <div v-if="expandedSections[table.key]" class="mt-4 bg-gray-700 rounded-xl shadow-lg overflow-hidden">
                    <table class="min-w-full">
                      <thead>
                        <tr class="bg-gray-600">
                          <th class="py-3 px-6 text-left">{{ table.header1 }}</th>
                          <th class="py-3 px-6 text-left">{{ table.header2 }}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(value, key) in selectedStat[table.dataKey] || {}" :key="key" class="border-t border-gray-600 hover:bg-gray-600 transition-colors duration-200">
                          <td class="py-3 px-6">{{ key }}</td>
                          <td class="py-3 px-6 text-yellow-400">{{ value || 0 }}</td>
                        </tr>
                        <tr v-if="!Object.keys(selectedStat[table.dataKey] || {}).length" class="border-t border-gray-600">
                          <td colspan="2" class="py-3 px-6 text-center">ไม่มีข้อมูล</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </transition>
              </div>
              <!-- Collapsible Table for Mechanical Resets -->
              <div class="mb-6">
                <button @click="toggleSection('mechanicalResets')" class="w-full flex justify-between items-center bg-gray-600 p-4 rounded-xl text-xl font-semibold hover:bg-gray-500 transition-colors duration-200">
                  <span>การรีเซ็ตเครื่องจักร</span>
                  <svg :class="{ 'rotate-180': expandedSections.mechanicalResets }" class="w-6 h-6 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <transition name="fade">
                  <div v-if="expandedSections.mechanicalResets" class="mt-4 bg-gray-700 rounded-xl shadow-lg overflow-hidden">
                    <table class="min-w-full">
                      <thead>
                        <tr class="bg-gray-600">
                          <th class="py-3 px-6 text-left">จำนวน</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr class="border-t border-gray-600 hover:bg-gray-600 transition-colors duration-200">
                          <td class="py-3 px-6 text-yellow-400 text-center">{{ selectedStat.mechanicalResets || 0 }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </transition>
              </div>
              <!-- Collapsible Table for Mechanical Upgrades -->
              <div class="mb-6">
                <button @click="toggleSection('mechanicalUpgrades')" class="w-full flex justify-between items-center bg-gray-600 p-4 rounded-xl text-xl font-semibold hover:bg-gray-500 transition-colors duration-200">
                  <span>การอัปเกรดเครื่องจักร</span>
                  <svg :class="{ 'rotate-180': expandedSections.mechanicalUpgrades }" class="w-6 h-6 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <transition name="fade">
                  <div v-if="expandedSections.mechanicalUpgrades" class="mt-4 bg-gray-700 rounded-xl shadow-lg overflow-hidden">
                    <table class="min-w-full">
                      <thead>
                        <tr class="bg-gray-600">
                          <th class="py-3 px-6 text-left">จำนวน</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr class="border-t border-gray-600 hover:bg-gray-600 transition-colors duration-200">
                          <td class="py-3 px-6 text-yellow-400 text-center">{{ selectedStat.mechanicalUpgrades || 0 }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </transition>
              </div>
              <!-- Collapsible Table for Equipment Upgrades -->
              <div class="mb-6">
                <button @click="toggleSection('equipmentUpgrades')" class="w-full flex justify-between items-center bg-gray-600 p-4 rounded-xl text-xl font-semibold hover:bg-gray-500 transition-colors duration-200">
                  <span>การอัปเกรดอุปกรณ์</span>
                  <svg :class="{ 'rotate-180': expandedSections.equipmentUpgrades }" class="w-6 h-6 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <transition name="fade">
                  <div v-if="expandedSections.equipmentUpgrades" class="mt-4 bg-gray-700 rounded-xl shadow-lg overflow-hidden">
                    <table class="min-w-full">
                      <thead>
                        <tr class="bg-gray-600">
                          <th class="py-3 px-6 text-left">จำนวน</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr class="border-t border-gray-600 hover:bg-gray-600 transition-colors duration-200">
                          <td class="py-3 px-6 text-yellow-400 text-center">{{ selectedStat.equipmentUpgrades || 0 }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </transition>
              </div>
            </section>

            <!-- ส่วนที่ 6: การเสียชีวิต -->
            <section class="mb-12">
              <h1 class="text-3xl font-bold mb-6">การเสียชีวิต</h1>
              <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                <div v-for="(item, index) in deathStats" :key="index" class="bg-gray-700 p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300">
                  <h2 class="text-xl font-semibold mb-2">{{ item.title }}</h2>
                  <p class="text-4xl text-yellow-400">{{ item.value }} {{ item.unit }}</p>
                </div>
              </div>
            </section>

            <!-- ส่วนที่ 7: การลอบสังหาร -->
            <section class="mb-12">
              <h1 class="text-3xl font-bold mb-6">การลอบสังหาร</h1>
              <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                <div v-for="(item, index) in assassinationStats" :key="index" class="bg-gray-700 p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300">
                  <h2 class="text-xl font-semibold mb-2">{{ item.title }}</h2>
                  <p class="text-4xl text-yellow-400">{{ item.value }} {{ item.unit }}</p>
                </div>
              </div>
              <!-- Collapsible Table for Survivor Assassination Dice Selections -->
              <div class="mb-6">
                <button @click="toggleSection('survivorAssassinationDiceSelections')" class="w-full flex justify-between items-center bg-gray-600 p-4 rounded-xl text-xl font-semibold hover:bg-gray-500 transition-colors duration-200">
                  <span>การเลือกเต๋าการลอบสังหารผู้รอดชีวิต</span>
                  <svg :class="{ 'rotate-180': expandedSections.survivorAssassinationDiceSelections }" class="w-6 h-6 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <transition name="fade">
                  <div v-if="expandedSections.survivorAssassinationDiceSelections" class="mt-4 bg-gray-700 rounded-xl shadow-lg overflow-hidden">
                    <table class="min-w-full">
                      <thead>
                        <tr class="bg-gray-600">
                          <th class="py-3 px-6 text-left">เต๋า</th>
                          <th class="py-3 px-6 text-left">จำนวนครั้ง</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(value, key) in selectedStat.survivorAssassinationDiceSelections || {}" :key="key" class="border-t border-gray-600 hover:bg-gray-600 transition-colors duration-200">
                          <td class="py-3 px-6">{{ key }}</td>
                          <td class="py-3 px-6 text-yellow-400">{{ value || 0 }}</td>
                        </tr>
                        <tr v-if="!Object.keys(selectedStat.survivorAssassinationDiceSelections || {}).length" class="border-t border-gray-600">
                          <td colspan="2" class="py-3 px-6 text-center">ไม่มีข้อมูล</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </transition>
              </div>
              <!-- Collapsible Table for Ghost Encounters During Assassination -->
              <div class="mb-6">
                <button @click="toggleSection('ghostEncountersDuringAssassination')" class="w-full flex justify-between items-center bg-gray-600 p-4 rounded-xl text-xl font-semibold hover:bg-gray-500 transition-colors duration-200">
                  <span>การพบผีระหว่างการลอบสังหาร</span>
                  <svg :class="{ 'rotate-180': expandedSections.ghostEncountersDuringAssassination }" class="w-6 h-6 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <transition name="fade">
                  <div v-if="expandedSections.ghostEncountersDuringAssassination" class="mt-4 bg-gray-700 rounded-xl shadow-lg overflow-hidden">
                    <table class="min-w-full">
                      <thead>
                        <tr class="bg-gray-600">
                          <th class="py-3 px-6 text-left">จำนวน</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr class="border-t border-gray-600 hover:bg-gray-600 transition-colors duration-200">
                          <td class="py-3 px-6 text-yellow-400 text-center">{{ selectedStat.ghostEncountersDuringAssassination || 0 }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </transition>
              </div>
              <!-- Collapsible Table for Trap Disarms -->
              <div class="mb-6">
                <button @click="toggleSection('trapDisarms')" class="w-full flex justify-between items-center bg-gray-600 p-4 rounded-xl text-xl font-semibold hover:bg-gray-500 transition-colors duration-200">
                  <span>การปลดกับดัก</span>
                  <svg :class="{ 'rotate-180': expandedSections.trapDisarms }" class="w-6 h-6 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <transition name="fade">
                  <div v-if="expandedSections.trapDisarms" class="mt-4 bg-gray-700 rounded-xl shadow-lg overflow-hidden">
                    <table class="min-w-full">
                      <thead>
                        <tr class="bg-gray-600">
                          <th class="py-3 px-6 text-left">จำนวน</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr class="border-t border-gray-600 hover:bg-gray-600 transition-colors duration-200">
                          <td class="py-3 px-6 text-yellow-400 text-center">{{ selectedStat.trapDisarms || 0 }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </transition>
              </div>
              <!-- Collapsible Table for Trap Destroyed By Bomb -->
              <div class="mb-6">
                <button @click="toggleSection('trapDestroyedByBomb')" class="w-full flex justify-between items-center bg-gray-600 p-4 rounded-xl text-xl font-semibold hover:bg-gray-500 transition-colors duration-200">
                  <span>กับดักที่ถูกทำลายโดยระเบิด</span>
                  <svg :class="{ 'rotate-180': expandedSections.trapDestroyedByBomb }" class="w-6 h-6 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <transition name="fade">
                  <div v-if="expandedSections.trapDestroyedByBomb" class="mt-4 bg-gray-700 rounded-xl shadow-lg overflow-hidden">
                    <table class="min-w-full">
                      <thead>
                        <tr class="bg-gray-600">
                          <th class="py-3 px-6 text-left">จำนวน</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr class="border-t border-gray-600 hover:bg-gray-600 transition-colors duration-200">
                          <td class="py-3 px-6 text-yellow-400 text-center">{{ selectedStat.trapDestroyedByBomb || 0 }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </transition>
              </div>
              <!-- Collapsible Table for Successful Ambush Attacks -->
              <div class="mb-6">
                <button @click="toggleSection('successfulAmbushAttacks')" class="w-full flex justify-between items-center bg-gray-600 p-4 rounded-xl text-xl font-semibold hover:bg-gray-500 transition-colors duration-200">
                  <span>การโจมตีซุ่มโจมตีสำเร็จ</span>
                  <svg :class="{ 'rotate-180': expandedSections.successfulAmbushAttacks }" class="w-6 h-6 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <transition name="fade">
                  <div v-if="expandedSections.successfulAmbushAttacks" class="mt-4 bg-gray-700 rounded-xl shadow-lg overflow-hidden">
                    <table class="min-w-full">
                      <thead>
                        <tr class="bg-gray-600">
                          <th class="py-3 px-6 text-left">จำนวน</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr class="border-t border-gray-600 hover:bg-gray-600 transition-colors duration-200">
                          <td class="py-3 px-6 text-yellow-400 text-center">{{ selectedStat.successfulAmbushAttacks || 0 }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </transition>
              </div>
              <!-- Collapsible Table for Successful Ambush Flees -->
              <div class="mb-6">
                <button @click="toggleSection('successfulAmbushFlees')" class="w-full flex justify-between items-center bg-gray-600 p-4 rounded-xl text-xl font-semibold hover:bg-gray-500 transition-colors duration-200">
                  <span>การหนีซุ่มโจมตีสำเร็จ</span>
                  <svg :class="{ 'rotate-180': expandedSections.successfulAmbushFlees }" class="w-6 h-6 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <transition name="fade">
                  <div v-if="expandedSections.successfulAmbushFlees" class="mt-4 bg-gray-700 rounded-xl shadow-lg overflow-hidden">
                    <table class="min-w-full">
                      <thead>
                        <tr class="bg-gray-600">
                          <th class="py-3 px-6 text-left">จำนวน</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr class="border-t border-gray-600 hover:bg-gray-600 transition-colors duration-200">
                          <td class="py-3 px-6 text-yellow-400 text-center">{{ selectedStat.successfulAmbushFlees || 0 }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </transition>
              </div>
              <!-- Collapsible Table for Ambush Attempts -->
              <div class="mb-6">
                <button @click="toggleSection('ambushAttempts')" class="w-full flex justify-between items-center bg-gray-600 p-4 rounded-xl text-xl font-semibold hover:bg-gray-500 transition-colors duration-200">
                  <span>การพยายามซุ่มโจมตี</span>
                  <svg :class="{ 'rotate-180': expandedSections.ambushAttempts }" class="w-6 h-6 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <transition name="fade">
                  <div v-if="expandedSections.ambushAttempts" class="mt-4 bg-gray-700 rounded-xl shadow-lg overflow-hidden">
                    <table class="min-w-full">
                      <thead>
                        <tr class="bg-gray-600">
                          <th class="py-3 px-6 text-left">จำนวน</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr class="border-t border-gray-600 hover:bg-gray-600 transition-colors duration-200">
                          <td class="py-3 px-6 text-yellow-400 text-center">{{ selectedStat.ambushAttempts || 0 }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </transition>
              </div>
              <!-- Collapsible Table for Bomb Installs -->
              <div class="mb-6">
                <button @click="toggleSection('bombInstalls')" class="w-full flex justify-between items-center bg-gray-600 p-4 rounded-xl text-xl font-semibold hover:bg-gray-500 transition-colors duration-200">
                  <span>การติดตั้งระเบิด</span>
                  <svg :class="{ 'rotate-180': expandedSections.bombInstalls }" class="w-6 h-6 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <transition name="fade">
                  <div v-if="expandedSections.bombInstalls" class="mt-4 bg-gray-700 rounded-xl shadow-lg overflow-hidden">
                    <table class="min-w-full">
                      <thead>
                        <tr class="bg-gray-600">
                          <th class="py-3 px-6 text-left">จำนวน</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr class="border-t border-gray-600 hover:bg-gray-600 transition-colors duration-200">
                          <td class="py-3 px-6 text-yellow-400 text-center">{{ selectedStat.bombInstalls || 0 }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </transition>
              </div>
              <!-- Collapsible Table for Ghost Activations -->
              <div class="mb-6">
                <button @click="toggleSection('ghostActivations')" class="w-full flex justify-between items-center bg-gray-600 p-4 rounded-xl text-xl font-semibold hover:bg-gray-500 transition-colors duration-200">
                  <span>การกระตุ้นผี</span>
                  <svg :class="{ 'rotate-180': expandedSections.ghostActivations }" class="w-6 h-6 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <transition name="fade">
                  <div v-if="expandedSections.ghostActivations" class="mt-4 bg-gray-700 rounded-xl shadow-lg overflow-hidden">
                    <table class="min-w-full">
                      <thead>
                        <tr class="bg-gray-600">
                          <th class="py-3 px-6 text-left">จำนวน</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr class="border-t border-gray-600 hover:bg-gray-600 transition-colors duration-200">
                          <td class="py-3 px-6 text-yellow-400 text-center">{{ selectedStat.ghostActivations || 0 }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </transition>
              </div>
              <!-- Collapsible Table for Ghost Cancellations -->
              <div class="mb-6">
                <button @click="toggleSection('ghostCancellations')" class="w-full flex justify-between items-center bg-gray-600 p-4 rounded-xl text-xl font-semibold hover:bg-gray-500 transition-colors duration-200">
                  <span>การยกเลิกผี</span>
                  <svg :class="{ 'rotate-180': expandedSections.ghostCancellations }" class="w-6 h-6 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <transition name="fade">
                  <div v-if="expandedSections.ghostCancellations" class="mt-4 bg-gray-700 rounded-xl shadow-lg overflow-hidden">
                    <table class="min-w-full">
                      <thead>
                        <tr class="bg-gray-600">
                          <th class="py-3 px-6 text-left">จำนวน</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr class="border-t border-gray-600 hover:bg-gray-600 transition-colors duration-200">
                          <td class="py-3 px-6 text-yellow-400 text-center">{{ selectedStat.ghostCancellations || 0 }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </transition>
              </div>
              <!-- Collapsible Table for Ghost Expirations -->
              <div class="mb-6">
                <button @click="toggleSection('ghostExpirations')" class="w-full flex justify-between items-center bg-gray-600 p-4 rounded-xl text-xl font-semibold hover:bg-gray-500 transition-colors duration-200">
                  <span>การหมดอายุผี</span>
                  <svg :class="{ 'rotate-180': expandedSections.ghostExpirations }" class="w-6 h-6 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <transition name="fade">
                  <div v-if="expandedSections.ghostExpirations" class="mt-4 bg-gray-700 rounded-xl shadow-lg overflow-hidden">
                    <table class="min-w-full">
                      <thead>
                        <tr class="bg-gray-600">
                          <th class="py-3 px-6 text-left">จำนวน</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr class="border-t border-gray-600 hover:bg-gray-600 transition-colors duration-200">
                          <td class="py-3 px-6 text-yellow-400 text-center">{{ selectedStat.ghostExpirations || 0 }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </transition>
              </div>
              <!-- Collapsible Table for Loot Cancellations -->
              <div class="mb-6">
                <button @click="toggleSection('lootCancellations')" class="w-full flex justify-between items-center bg-gray-600 p-4 rounded-xl text-xl font-semibold hover:bg-gray-500 transition-colors duration-200">
                  <span>การยกเลิก loot</span>
                  <svg :class="{ 'rotate-180': expandedSections.lootCancellations }" class="w-6 h-6 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <transition name="fade">
                  <div v-if="expandedSections.lootCancellations" class="mt-4 bg-gray-700 rounded-xl shadow-lg overflow-hidden">
                    <table class="min-w-full">
                      <thead>
                        <tr class="bg-gray-600">
                          <th class="py-3 px-6 text-left">จำนวน</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr class="border-t border-gray-600 hover:bg-gray-600 transition-colors duration-200">
                          <td class="py-3 px-6 text-yellow-400 text-center">{{ selectedStat.lootCancellations || 0 }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </transition>
              </div>
              <!-- Collapsible Table for Loot Installations -->
              <div class="mb-6">
                <button @click="toggleSection('lootInstallations')" class="w-full flex justify-between items-center bg-gray-600 p-4 rounded-xl text-xl font-semibold hover:bg-gray-500 transition-colors duration-200">
                  <span>การติดตั้ง loot</span>
                  <svg :class="{ 'rotate-180': expandedSections.lootInstallations }" class="w-6 h-6 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <transition name="fade">
                  <div v-if="expandedSections.lootInstallations" class="mt-4 bg-gray-700 rounded-xl shadow-lg overflow-hidden">
                    <table class="min-w-full">
                      <thead>
                        <tr class="bg-gray-600">
                          <th class="py-3 px-6 text-left">จำนวน</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr class="border-t border-gray-600 hover:bg-gray-600 transition-colors duration-200">
                          <td class="py-3 px-6 text-yellow-400 text-center">{{ selectedStat.lootInstallations || 0 }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </transition>
              </div>
              <!-- Collapsible Table for Waiting Loot Expirations -->
              <div class="mb-6">
                <button @click="toggleSection('waitingLootExpirations')" class="w-full flex justify-between items-center bg-gray-600 p-4 rounded-xl text-xl font-semibold hover:bg-gray-500 transition-colors duration-200">
                  <span>การหมดอายุ loot ที่รอ</span>
                  <svg :class="{ 'rotate-180': expandedSections.waitingLootExpirations }" class="w-6 h-6 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <transition name="fade">
                  <div v-if="expandedSections.waitingLootExpirations" class="mt-4 bg-gray-700 rounded-xl shadow-lg overflow-hidden">
                    <table class="min-w-full">
                      <thead>
                        <tr class="bg-gray-600">
                          <th class="py-3 px-6 text-left">จำนวน</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr class="border-t border-gray-600 hover:bg-gray-600 transition-colors duration-200">
                          <td class="py-3 px-6 text-yellow-400 text-center">{{ selectedStat.waitingLootExpirations || 0 }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </transition>
              </div>
            </section>

            <!-- ส่วนที่ 8: การซื้อและการเติมเงิน -->
            <section class="mb-12">
              <h1 class="text-3xl font-bold mb-6">การซื้อและการเติมเงิน</h1>
              <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                <div v-for="(item, index) in topupStats" :key="index" class="bg-gray-700 p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300">
                  <h2 class="text-xl font-semibold mb-2">{{ item.title }}</h2>
                  <p class="text-4xl text-yellow-400">{{ item.value }} {{ item.unit }}</p>
                </div>
              </div>
              <!-- Collapsible Table for Total Diamond From Item Sales -->
              <div class="mb-6">
                <button @click="toggleSection('totalDiamondFromItemSales')" class="w-full flex justify-between items-center bg-gray-600 p-4 rounded-xl text-xl font-semibold hover:bg-gray-500 transition-colors duration-200">
                  <span>เพชรที่ได้จากการขายไอเทม</span>
                  <svg :class="{ 'rotate-180': expandedSections.totalDiamondFromItemSales }" class="w-6 h-6 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <transition name="fade">
                  <div v-if="expandedSections.totalDiamondFromItemSales" class="mt-4 bg-gray-700 rounded-xl shadow-lg overflow-hidden">
                    <table class="min-w-full">
                      <thead>
                        <tr class="bg-gray-600">
                          <th class="py-3 px-6 text-left">จำนวน</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr class="border-t border-gray-600 hover:bg-gray-600 transition-colors duration-200">
                          <td class="py-3 px-6 text-yellow-400 text-center">{{ selectedStat.totalDiamondFromItemSales || 0 }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </transition>
              </div>
              <!-- Collapsible Table for Merchants Killed -->
              <div class="mb-6">
                <button @click="toggleSection('merchantsKilled')" class="w-full flex justify-between items-center bg-gray-600 p-4 rounded-xl text-xl font-semibold hover:bg-gray-500 transition-colors duration-200">
                  <span>พ่อค้าที่ถูกฆ่า</span>
                  <svg :class="{ 'rotate-180': expandedSections.merchantsKilled }" class="w-6 h-6 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <transition name="fade">
                  <div v-if="expandedSections.merchantsKilled" class="mt-4 bg-gray-700 rounded-xl shadow-lg overflow-hidden">
                    <table class="min-w-full">
                      <thead>
                        <tr class="bg-gray-600">
                          <th class="py-3 px-6 text-left">จำนวน</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr class="border-t border-gray-600 hover:bg-gray-600 transition-colors duration-200">
                          <td class="py-3 px-6 text-yellow-400 text-center">{{ selectedStat.merchantsKilled || 0 }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </transition>
              </div>
            </section>
          </div>
          <!-- Modal Footer -->
          <div class="p-4 border-t border-gray-700 flex justify-end">
            <button @click="closeDetailsModal" class="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-500 transition-colors duration-200">
              ปิด
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { projectFirestore } from '@/firebase/config';
import { ref, computed, onMounted } from 'vue';

export default {
  setup() {
    const stats = ref([]);
    const loading = ref(true);
    const error = ref(null);
    const currentPage = ref(1);
    const perPage = ref(10);
    const showModal = ref(false);
    const selectedStat = ref({});
    const expandedSections = ref({
      itemsLost: false,
      tattoosLost: false,
      skillsLost: false,
      roomsLostByCount: false,
      decomposedItemsObtained: false,
      skillsLearnedByName: false,
      mapsReadByName: false,
      tattoosAppliedByName: false,
      titlesUnlockedByName: false,
      titlesUsedByName: false,
      explorationsByMap: false,
      resourcesUsed: false,
      foodsPurchased: false,
      resourcesLostByPlayersInSurvivorAssassinations: false,
      roomsUnlocked: false,
      serverTaxFromSurvivorAssassinations: false,
      totalResourcesUsedForAssassinations: false,
      totalResourcesUsedForRoomUnlocks: false,
      survivorAssassinationDiceSelections: false,
      itemsPurchased: false,
      totalResourcesUsedForPurchases: false,
      explorationsByBasement: false,
      criminalsKilled: false,
      missionCompletionsById: false,
      itemsCrafted: false,
      itemsFromMysteriousBox: false,
      healedByItem: false,
      trapInstallationsByItem: false,
      trapCancellationsByType: false,
      rankBonusRewards: false,
      totalResourcesUsedForStorageUpgrades: false,
      mysteriousItemsReceived: false,
      dailyMissionCompletionsById: false,
      weeklyMissionCompletionsById: false,
      ghostActivationsByItem: false,
      ghostCancellationsByType: false,
      ghostExpirationsByType: false,
      bombInstallsByItem: false,
      itemsListedInMarketplace: false,
      itemsReturnedFromMarketplace: false,
      itemsTurnedToTrash: false,
      lootCancellationsByItem: false,
      lootInstallationsByItem: false,
      waitingLootExpirationsByItem: false,
      marketplaceDiamondsTraded: false,
      marketplaceItemsSold: false,
      marketplaceShopsClosed: false,
      marketplaceShopsCreated: false,
      marketplaceTaxCollected: false,
      mechanicalResets: false,
      mechanicalUpgrades: false,
      equipmentUpgrades: false,
      trapDisarmsByItem: false,
      ghostEncountersDuringAssassination: false,
      trapDisarms: false,
      trapDestroyedByBomb: false,
      successfulAmbushAttacks: false,
      successfulAmbushFlees: false,
      ambushAttempts: false,
      bombInstalls: false,
      ghostActivations: false,
      ghostCancellations: false,
      ghostExpirations: false,
      lootCancellations: false,
      lootInstallations: false,
      waitingLootExpirations: false,
      totalDiamondFromItemSales: false,
      merchantsKilled: false,
      occupiersDefeated: false,
    });

    // Fetch stats from Firestore
    const fetchStats = async () => {
      try {
        const snapshot = await projectFirestore.collection('stats').orderBy('createdAt', 'desc').get();
        stats.value = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        loading.value = false;
      } catch (err) {
        console.error('Error fetching stats:', err);
        error.value = 'เกิดข้อผิดพลาดในการโหลดข้อมูล';
        loading.value = false;
      }
    };

    // Pagination
    const totalPages = computed(() => Math.ceil(stats.value.length / perPage.value));
    const paginatedStats = computed(() => {
      const start = (currentPage.value - 1) * perPage.value;
      const end = start + perPage.value;
      return stats.value.slice(start, end);
    });

    const prevPage = () => {
      if (currentPage.value > 1) currentPage.value--;
    };

    const nextPage = () => {
      if (currentPage.value < totalPages.value) currentPage.value++;
    };

    // Modal Handling
    const openDetailsModal = (stat) => {
      selectedStat.value = stat;
      Object.keys(expandedSections.value).forEach(key => {
        expandedSections.value[key] = false;
      });
      showModal.value = true;
    };

    const closeDetailsModal = () => {
      showModal.value = false;
      selectedStat.value = {};
    };

    // Activity Stats
    const activityStats = computed(() => [
      { title: 'การอัปเกรดสถิติ', value: selectedStat.value.statUpgrades || 0, unit: 'ครั้ง' },
      { title: 'การโอนไอเทม', value: selectedStat.value.itemTransfers || 0, unit: 'ครั้ง' },
      { title: 'การสำรวจแผนที่', value: selectedStat.value.mapExplorations || 0, unit: 'ครั้ง' },
      { title: 'การต่อสู้กับซอมบี้', value: selectedStat.value.zombieBattles || 0, unit: 'ครั้ง' },
      { title: 'ผู้เล่นที่ถูกซอมบี้ฆ่า', value: selectedStat.value.playersKilledByZombies || 0, unit: 'คน' },
      { title: 'ซอมบี้ที่ถูกฆ่า', value: selectedStat.value.zombiesKilled || 0, unit: 'ตัว' },
      { title: 'พ่อค้าที่ถูกฆ่า', value: selectedStat.value.merchantsKilled || 0, unit: 'คน' },
      { title: 'พลังงานที่ใช้ทั้งหมด', value: selectedStat.value.totalEnergyUsed || 0, unit: 'หน่วย' },
      { title: 'การพยายามหนี', value: selectedStat.value.fleeAttempts || 0, unit: 'ครั้ง' },
      { title: 'การหนีสำเร็จ', value: selectedStat.value.successfulFlees || 0, unit: 'ครั้ง' },
      { title: 'การกลับบ้านสำเร็จ', value: selectedStat.value.successfulReturnsHome || 0, unit: 'ครั้ง' },
      { title: 'การเคลื่อนย้ายห้องทั้งหมด', value: selectedStat.value.totalRoomMovements || 0, unit: 'ครั้ง' },
      { title: 'การสำรวจชั้นใต้ดิน', value: selectedStat.value.basementExplorations || 0, unit: 'ครั้ง' },
      { title: 'การต่อสู้อาชญากร', value: selectedStat.value.criminalBattles || 0, unit: 'ครั้ง' },
      { title: 'การต่อสู้กับหุ่นยนต์', value: selectedStat.value.robotBattles || 0, unit: 'ครั้ง' },
      { title: 'หุ่นยนต์ที่ถูกฆ่า', value: selectedStat.value.robotsKilled || 0, unit: 'ตัว' },
      { title: 'ผู้เล่นที่ถูกหุ่นยนต์ฆ่า', value: selectedStat.value.playersKilledByRobots || 0, unit: 'คน' },
      { title: 'ผู้เล่นที่ถูกอาชญากรฆ่า', value: selectedStat.value.playersKilledByCriminals || 0, unit: 'คน' },
      { title: 'ผู้เล่นที่ถูกผีฆ่า', value: selectedStat.value.playersKilledByGhosts || 0, unit: 'คน' },
      { title: 'ผู้เล่นที่ถูกพ่อค้าฆ่า', value: selectedStat.value.playersKilledByMerchants || 0, unit: 'คน' },
      { title: 'การต่อสู้กับผู้เล่น', value: selectedStat.value.playerBattles || 0, unit: 'ครั้ง' },
      { title: 'การพบผู้เล่น', value: selectedStat.value.playerEncounters || 0, unit: 'ครั้ง' },
      { title: 'การสำเร็จภารกิจ', value: selectedStat.value.missionCompletions || 0, unit: 'ครั้ง' },
      { title: 'การติดตั้งกับดัก', value: selectedStat.value.trapInstallations || 0, unit: 'ครั้ง' },
      { title: 'การยกเลิกกับดัก', value: selectedStat.value.trapCancellations || 0, unit: 'ครั้ง' },
      { title: 'การกระตุ้นกับดักระหว่างการลอบสังหาร', value: selectedStat.value.trapTriggersDuringAssassination || 0, unit: 'ครั้ง' },
      { title: 'การลดจำนวนการใช้กับดัก', value: selectedStat.value.trapUseCountDecrements || 0, unit: 'ครั้ง' },
      { title: 'การเติมเชื้อเพลิงยานพาหนะ', value: selectedStat.value.vehicleRefuels || 0, unit: 'ครั้ง' },
      { title: 'การอัพเกรดหุ่นยนต์', value: selectedStat.value.robotUpgrades || 0, unit: 'ครั้ง' },
      { title: 'การเปิดกล่องปริศนาทั้งหมด', value: selectedStat.value.totalMysteriousBoxOpened || 0, unit: 'ครั้ง' },
      { title: 'การโพสต์ล่าสุด', value: selectedStat.value.lastPostTimestamp || 'N/A', unit: '' },
      { title: 'อัพเดตล่าสุด', value: selectedStat.value.updatedAt || 'N/A', unit: '' },
      { title: 'จำนวนโพสต์ทั้งหมด', value: selectedStat.value.totalPosts || 0, unit: 'ครั้ง' },
      { title: 'จำนวนความคิดเห็นทั้งหมด', value: selectedStat.value.totalComments || 0, unit: 'ครั้ง' },
      { title: 'จำนวนหัวข้อทั้งหมด', value: selectedStat.value.totalTopics || 0, unit: 'ครั้ง' },
    ]);

    // Item Management Stats
    const itemStats = computed(() => [
      { title: 'ไอเทมที่เพิ่มเข้าในกระเป๋า', value: selectedStat.value.itemsAddedToBag || 0, unit: 'ชิ้น' },
      { title: 'ไอเทมที่เก็บได้ทั้งหมด', value: selectedStat.value.totalItemsCollected || 0, unit: 'ชิ้น' },
      { title: 'น้ำหนักที่เพิ่มทั้งหมด', value: selectedStat.value.totalWeightAdded || 0, unit: 'หน่วย' },
      { title: 'การยอมรับการลงโทษ', value: selectedStat.value.punishmentAcceptances || 0, unit: 'ครั้ง' },
      { title: 'การลงโทษสำหรับเหตุการณ์ที่หมดอายุ', value: selectedStat.value.totalPunishmentsForExpiredEvents || 0, unit: 'ครั้ง' },
      { title: 'ไอเทมที่ทิ้ง', value: selectedStat.value.itemsDiscarded || 0, unit: 'ชิ้น' },
      { title: 'ไอเทมที่สวมใส่', value: selectedStat.value.itemsEquipped || 0, unit: 'ชิ้น' },
      { title: 'ไอเทมที่ถอดออก', value: selectedStat.value.itemsUnequipped || 0, unit: 'ชิ้น' },
      { title: 'การสลายไอเทมสำเร็จ', value: selectedStat.value.successfulDecompositions || 0, unit: 'ครั้ง' },
      { title: 'การสลายไอเทมล้มเหลว', value: selectedStat.value.failedDecompositions || 0, unit: 'ครั้ง' },
      { title: 'ไอเทมที่ย้ายไปยังที่เก็บ', value: selectedStat.value.itemsMovedToStorage || 0, unit: 'ชิ้น' },
      { title: 'ไอเทมที่ย้ายไปยังกระเป๋า', value: selectedStat.value.itemsMovedToBag || 0, unit: 'ชิ้น' },
      { title: 'ไอเทมที่ได้จากคลังปริศนา', value: selectedStat.value.mysteriousItemsClaimed || 0, unit: 'ชิ้น' },
      { title: 'การรีเซ็ตสถิติ', value: selectedStat.value.statResets || 0, unit: 'ครั้ง' },
      { title: 'การรักษา', value: selectedStat.value.healActions || 0, unit: 'ครั้ง' },
      { title: 'การพยายามรักษาล้มเหลว', value: selectedStat.value.failedHealAttempts || 0, unit: 'ครั้ง' },
      { title: 'HP ที่รักษาทั้งหมด', value: selectedStat.value.totalHpHealed || 0, unit: 'หน่วย' },
      { title: 'ไวรัสที่รักษาทั้งหมด', value: selectedStat.value.totalVirusHealed || 0, unit: 'หน่วย' },
    ]);

    // Collapsible Tables Configuration
    const collapsibleTables = [
      { key: 'itemsLost', title: 'ไอเทมที่สูญเสีย', dataKey: 'itemsLost', header1: 'ไอเทม', header2: 'จำนวน' },
      { key: 'tattoosLost', title: 'รอยสักที่สูญเสีย', dataKey: 'tattoosLost', header1: 'รอยสัก', header2: 'จำนวน' },
      { key: 'skillsLost', title: 'ทักษะที่สูญเสีย', dataKey: 'skillsLost', header1: 'ทักษะ', header2: 'จำนวน' },
      { key: 'roomsLostByCount', title: 'ห้องที่สูญเสียตามจำนวน', dataKey: 'roomsLostByCount', header1: 'จำนวนห้อง', header2: 'จำนวนครั้ง' },
      { key: 'decomposedItemsObtained', title: 'ไอเทมที่ได้จากการสลาย', dataKey: 'decomposedItemsObtained', header1: 'ไอเทม', header2: 'จำนวน' },
      { key: 'foodsPurchased', title: 'อาหารที่ซื้อ', dataKey: 'foodsPurchased', header1: 'อาหาร', header2: 'จำนวน' },
      { key: 'resourcesLostByPlayersInSurvivorAssassinations', title: 'ทรัพยากรที่สูญเสียจากการลอบสังหารผู้รอดชีวิต', dataKey: 'resourcesLostByPlayersInSurvivorAssassinations', header1: 'ทรัพยากร', header2: 'จำนวน' },
      { key: 'roomsUnlocked', title: 'ห้องที่ปลดล็อก', dataKey: 'roomsUnlocked', header1: 'ห้อง', header2: 'จำนวน' },
      { key: 'serverTaxFromSurvivorAssassinations', title: 'ภาษีเซิร์ฟเวอร์จากการลอบสังหารผู้รอดชีวิต', dataKey: 'serverTaxFromSurvivorAssassinations', header1: 'ประเภท', header2: 'จำนวน' },
      { key: 'totalResourcesUsedForAssassinations', title: 'ทรัพยากรที่ใช้ในการลอบสังหาร', dataKey: 'totalResourcesUsedForAssassinations', header1: 'ทรัพยากร', header2: 'จำนวน' },
      { key: 'totalResourcesUsedForRoomUnlocks', title: 'ทรัพยากรที่ใช้ในการปลดล็อกห้อง', dataKey: 'totalResourcesUsedForRoomUnlocks', header1: 'ทรัพยากร', header2: 'จำนวน' },
      { key: 'resourcesUsed', title: 'ทรัพยากรที่ใช้', dataKey: 'resourcesUsed', header1: 'ทรัพยากร', header2: 'จำนวน' },
      { key: 'mysteriousItemsReceived', title: 'ไอเทมที่ได้รับจากคลังปริศนา', dataKey: 'mysteriousItemsReceived', header1: 'ไอเทม', header2: 'จำนวน' },
    ];

    // Purchase and Sale Stats
    const purchaseSaleStats = computed(() => [
      { title: 'ไอเทมที่ขาย', value: selectedStat.value.itemsSold || 0, unit: 'ชิ้น' },
      { title: 'เงินที่ได้จากการขายไอเทม', value: selectedStat.value.totalSilverFromItemSales || 0, unit: 'หน่วย' },
      { title: 'ทองคำที่ได้จากการขายไอเทม', value: selectedStat.value.totalGoldFromItemSales || 0, unit: 'หน่วย' },
      { title: 'เพชรที่ได้จากการขายไอเทม', value: selectedStat.value.totalDiamondFromItemSales || 0, unit: 'หน่วย' },
    ]);

    // Collapsible Purchase/Sale Tables Configuration
    const collapsiblePurchaseSaleTables = [
      { key: 'itemsPurchased', title: 'ไอเทมที่ซื้อ', dataKey: 'itemsPurchased', header1: 'ไอเทม', header2: 'จำนวน' },
      { key: 'totalResourcesUsedForPurchases', title: 'ทรัพยากรที่ใช้ในการซื้อ', dataKey: 'totalResourcesUsedForPurchases', header1: 'ทรัพยากร', header2: 'จำนวน' },
    ];

    // Upgrade Stats
    const upgradeStats = computed(() => [
      { title: 'การเปลี่ยนลักษณะ', value: selectedStat.value.traitChanges || 0, unit: 'ครั้ง' },
      { title: 'เงินที่ใช้ในการเปลี่ยนลักษณะ', value: selectedStat.value.cashUsedForTraitChanges || 0, unit: 'หน่วย' },
      { title: 'การเปลี่ยนลักษณะล้มเหลว', value: selectedStat.value.failedTraitChanges || 0, unit: 'ครั้ง' },
      { title: 'ทักษะที่เรียนรู้', value: selectedStat.value.skillsLearned || 0, unit: 'ครั้ง' },
      { title: 'การเรียนรู้ทักษะล้มเหลว', value: selectedStat.value.failedSkillLearns || 0, unit: 'ครั้ง' },
      { title: 'การอ่านแผนที่', value: selectedStat.value.mapsRead || 0, unit: 'ครั้ง' },
      { title: 'การอ่านแผนที่ล้มเหลว', value: selectedStat.value.failedMapReads || 0, unit: 'ครั้ง' },
      { title: 'การสักรอยสัก', value: selectedStat.value.tattoosApplied || 0, unit: 'ครั้ง' },
      { title: 'เงินที่ใช้ในการสักรอยสัก', value: selectedStat.value.cashUsedForTattoos || 0, unit: 'หน่วย' },
      { title: 'เพชรที่ใช้ในการสักรอยสัก', value: selectedStat.value.diamondsUsedForTattoos || 0, unit: 'หน่วย' },
      { title: 'ทองที่ใช้ในการสักรอยสัก', value: selectedStat.value.goldUsedForTattoos || 0, unit: 'หน่วย' },
      { title: 'เงินที่ใช้ในการสักรอยสัก', value: selectedStat.value.silverUsedForTattoos || 0, unit: 'หน่วย' },
      { title: 'การสักรอยสักล้มเหลว', value: selectedStat.value.failedTattooAttempts || 0, unit: 'ครั้ง' },
      { title: 'ตำแหน่งที่ปลดล็อก', value: selectedStat.value.titlesUnlocked || 0, unit: 'ครั้ง' },
      { title: 'ทองที่ใช้ในการปลดล็อกตำแหน่ง', value: selectedStat.value.goldUsedForTitles || 0, unit: 'หน่วย' },
      { title: 'เงินที่ใช้ในการปลดล็อกตำแหน่ง', value: selectedStat.value.silverUsedForTitles || 0, unit: 'หน่วย' },
      { title: 'การปลดล็อกตำแหน่งล้มเหลว', value: selectedStat.value.failedTitleUnlocks || 0, unit: 'ครั้ง' },
      { title: 'ตำแหน่งที่ใช้', value: selectedStat.value.titlesUsed || 0, unit: 'ครั้ง' },
      { title: 'การใช้ตำแหน่งล้มเหลว', value: selectedStat.value.failedTitleUses || 0, unit: 'ครั้ง' },
      { title: 'การรักษาสุขภาพ', value: selectedStat.value.healthHeals || 0, unit: 'ครั้ง' },
      { title: 'การรักษาสุขภาพล้มเหลว', value: selectedStat.value.failedHealthHeals || 0, unit: 'ครั้ง' },
      { title: 'การรักษาการติดเชื้อ', value: selectedStat.value.infectionHeals || 0, unit: 'ครั้ง' },
      { title: 'เพิ่มพลังงานด้วยเงิน', value: selectedStat.value.energyIncreaseByCashCount || 0, unit: 'ครั้ง' },
      { title: 'รักษาด้วยเงิน', value: selectedStat.value.healByCashCount || 0, unit: 'ครั้ง' },
      { title: 'เงินที่ใช้สำหรับอัปเกรดกระเป๋า', value: selectedStat.value.totalCashUsedForBagUpgrades || 0, unit: 'หน่วย' },
      { title: 'เงินที่ใช้สำหรับอัปเกรดที่เก็บ', value: selectedStat.value.totalCashUsedForStorageUpgrades || 0, unit: 'หน่วย' },
    ]);

    // Collapsible Upgrade Tables Configuration
    const collapsibleUpgradeTables = [
      { key: 'skillsLearnedByName', title: 'ทักษะที่เรียนรู้ตามชื่อ', dataKey: 'skillsLearnedByName', header1: 'ทักษะ', header2: 'จำนวน' },
      { key: 'mapsReadByName', title: 'แผนที่ที่อ่านตามชื่อ', dataKey: 'mapsReadByName', header1: 'แผนที่', header2: 'จำนวน' },
      { key: 'tattoosAppliedByName', title: 'รอยสักที่สักตามชื่อ', dataKey: 'tattoosAppliedByName', header1: 'รอยสัก', header2: 'จำนวน' },
      { key: 'titlesUnlockedByName', title: 'ตำแหน่งที่ปลดล็อกตามชื่อ', dataKey: 'titlesUnlockedByName', header1: 'ตำแหน่ง', header2: 'จำนวน' },
      { key: 'titlesUsedByName', title: 'ตำแหน่งที่ใช้ตามชื่อ', dataKey: 'titlesUsedByName', header1: 'ตำแหน่ง', header2: 'จำนวน' },
    ];

    // Death Stats
    const deathStats = computed(() => [
      { title: 'ผู้เล่นที่เสียชีวิตจากความหิว', value: selectedStat.value.playersDiedFromStarvation || 0, unit: 'คน' },
      { title: 'ผู้เล่นที่เสียชีวิตจากติดเชื้อ', value: selectedStat.value.playersDiedFromInfection || 0, unit: 'คน' },
      { title: 'ผู้เล่นที่เสียชีวิตจากความไม่มีความสุข', value: selectedStat.value.playersDiedFromUnhappiness || 0, unit: 'คน' },
    ]);

    // Assassination Stats
    const assassinationStats = computed(() => [
      { title: 'การลอบสังหารสำเร็จ', value: selectedStat.value.successfulAssassinations || 0, unit: 'ครั้ง' },
      { title: 'การลอบสังหารทั้งหมด', value: selectedStat.value.totalAssassinationAttempts || 0, unit: 'ครั้ง' },
      { title: 'การลอบสังหารผู้รอดชีวิตสำเร็จ', value: selectedStat.value.successfulSurvivorAssassinations || 0, unit: 'ครั้ง' },
      { title: 'การลอบสังหารผู้รอดชีวิตล้มเหลว', value: selectedStat.value.failedSurvivorAssassinations || 0, unit: 'ครั้ง' },
      { title: 'การลอบสังหารผู้รอดชีวิตทั้งหมด', value: selectedStat.value.totalSurvivorAssassinationAttempts || 0, unit: 'ครั้ง' },
      { title: 'ระดับความโกรธที่ใช้ในการลอบสังหารผู้รอดชีวิต', value: selectedStat.value.totalFuryLevelUsedForSurvivorAssassinations || 0, unit: 'หน่วย' },
    ]);

    // Top-up Stats
    const topupStats = computed(() => [
      { title: 'เงินที่ใช้สำหรับเพิ่มพลังงาน', value: selectedStat.value.totalCashUsedForEnergy || 0, unit: 'หน่วย' },
      { title: 'เงินที่ใช้สำหรับการรักษา', value: selectedStat.value.totalCashUsedForHeal || 0, unit: 'หน่วย' },
      { title: 'เงินที่ใช้สำหรับอัปเกรดที่เก็บ', value: selectedStat.value.totalCashUsedForStorageUpgrades || 0, unit: 'หน่วย' },
      { title: 'จำนวนการเติมเงินทั้งหมด', value: selectedStat.value.totalTopupRequests || 0, unit: 'ครั้ง' },
      { title: 'จำนวนการเติมเงินที่อนุมัติ', value: selectedStat.value.totalTopupRequestsApproved || 0, unit: 'ครั้ง' },
      { title: 'ยอดเงินเติมทั้งหมด', value: selectedStat.value.totalTopupAmount || 0, unit: 'หน่วย' },
      { title: 'เงินที่ได้รับจากการเติมเงิน', value: selectedStat.value.totalCashReceivedFromTopups || 0, unit: 'หน่วย' },
      { title: 'เงินที่เติมเข้ามา', value: selectedStat.value.totalAmountToppedUp || 0, unit: 'หน่วย' },
      { title: 'เงินที่ใช้ซื้ออาหาร', value: selectedStat.value.totalSilverUsedForFoodPurchases || 0, unit: 'หน่วย' },
      { title: 'เงินที่ใช้รักษาการติดเชื้อ', value: selectedStat.value.totalSilverUsedForInfectionHeals || 0, unit: 'หน่วย' },
      { title: 'เงินที่ใช้ในการลอบสังหารผู้รอดชีวิต', value: selectedStat.value.totalSilverUsedForSurvivorAssassinations || 0, unit: 'หน่วย' },
      { title: 'เงินที่ใช้ทั้งหมด', value: selectedStat.value.totalSilverUsed || 0, unit: 'หน่วย' },
      { title: 'เพชรที่ใช้สำหรับกล่องปริศนา', value: selectedStat.value.totalDiamondsUsedForMysteriousBox || 0, unit: 'หน่วย' },
    ]);

    // Toggle function for collapsible sections
    const toggleSection = (key) => {
      expandedSections.value[key] = !expandedSections.value[key];
    };

    // Date Formatting
    const formatDate = (timestamp) => {
      if (!timestamp) return 'N/A';
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleString('th-TH', { timeZone: 'Asia/Bangkok' });
    };

    const formatTimestamp = (timestamp) => {
      if (!timestamp) return 'N/A';
      return new Date(timestamp).toLocaleString('th-TH', { timeZone: 'Asia/Bangkok' });
    };

    // Fetch data on mount
    onMounted(fetchStats);

    return {
      stats,
      loading,
      error,
      currentPage,
      perPage,
      totalPages,
      paginatedStats,
      prevPage,
      nextPage,
      showModal,
      selectedStat,
      activityStats,
      itemStats,
      collapsibleTables,
      purchaseSaleStats,
      collapsiblePurchaseSaleTables,
      upgradeStats,
      collapsibleUpgradeTables,
      deathStats,
      assassinationStats,
      topupStats,
      expandedSections,
      openDetailsModal,
      closeDetailsModal,
      toggleSection,
      formatDate,
      formatTimestamp,
    };
  },
};
</script>

<style scoped>
/* Smooth transitions for modal and collapsible sections */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* Ensure Tailwind utilities are applied correctly */
.bg {
  background: linear-gradient(to bottom, #1f2937, #111827);
}

/* Ensure table cells don't wrap unnecessarily */
th, td {
  white-space: nowrap;
}
</style>